<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <!-- Loading State -->
      <div v-if="isLoading" class="space-y-4">
        <div class="w-16 h-16 mx-auto">
          <svg class="w-16 h-16 animate-spin text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900">Connecting to Google Calendar...</h2>
        <p class="text-gray-600">Please wait while we set up your calendar integration.</p>
      </div>

      <!-- Success State -->
      <div v-else-if="isSuccess" class="space-y-4">
        <div class="w-16 h-16 mx-auto">
          <svg class="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900">Successfully Connected!</h2>
        <p class="text-gray-600">Your Google Calendar is now connected. You'll be redirected shortly.</p>
        <div class="mt-6">
          <button 
            @click="redirectToSessions"
            class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Sessions
          </button>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="space-y-4">
        <div class="w-16 h-16 mx-auto">
          <svg class="w-16 h-16 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900">Connection Failed</h2>
        <p class="text-gray-600">{{ error }}</p>
        <div class="mt-6 space-y-2">
          <button 
            @click="retryConnection"
            class="w-full px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
          <button 
            @click="redirectToSessions"
            class="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Go to Sessions
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { auth, onAuthStateChanged, db, doc, getDoc } from '@/firebase'
import { exchangeCodeForTokens, storeGoogleCalendarTokens } from '@/services/googleCalendarApi'
import type { User } from 'firebase/auth'

const router = useRouter()
const isLoading = ref(true)
const isSuccess = ref(false)
const error = ref('')

// Handle OAuth callback
const handleOAuthCallback = async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const errorParam = urlParams.get('error')

  if (errorParam) {
    error.value = `OAuth error: ${errorParam}`
    isLoading.value = false
    return
  }

  if (!code) {
    error.value = 'No authorization code received'
    isLoading.value = false
    return
  }

  try {
    // Wait for user to be authenticated
    const user = await new Promise<User | null>((resolve) => {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          resolve(user)
        }
      })
    })

    if (!user) {
      throw new Error('User not authenticated')
    }

    // Exchange code for tokens
    const tokens = await exchangeCodeForTokens(code)
    
    // Store tokens in Firestore
    await storeGoogleCalendarTokens(user.uid, tokens)
    
    isSuccess.value = true
    isLoading.value = false

    // Redirect after 3 seconds
    setTimeout(() => {
      redirectToSessions()
    }, 3000)

  } catch (err) {
    console.error('OAuth callback error:', err)
    error.value = err instanceof Error ? err.message : 'Failed to connect Google Calendar'
    isLoading.value = false
  }
}

// Redirect to sessions page based on user role
const redirectToSessions = async () => {
  if (!auth.currentUser) {
    router.push('/login')
    return
  }

  try {
    // Get user's role from Firestore
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const userType = userData.userType || 'student'
      const applicationStatus = userData.applicationStatus || 'pending'
      
      // Redirect based on user type and status
      if (userType === 'tutor') {
        if (applicationStatus === 'approved' && userData.profileStatus === 'approved') {
          router.push('/tutor/sessions')
        } else {
          router.push('/tutor/application-status')
        }
      } else {
        // Student logic
        if (applicationStatus === 'rejected') {
          router.push('/student/rejection')
        } else if (applicationStatus === 'approved' || applicationStatus === 'active' || applicationStatus === 'Active') {
          router.push('/student/sessions')
        } else {
          router.push('/student/application-status')
        }
      }
    } else {
      // Default to student application status if no user document found
      router.push('/student/application-status')
    }
  } catch (error) {
    console.error('Error getting user role:', error)
    // Default to student application status on error
    router.push('/student/application-status')
  }
}

// Retry connection
const retryConnection = async () => {
  await redirectToSessions()
}

// Handle OAuth callback on mount
onMounted(() => {
  handleOAuthCallback()
})
</script>
